import type { ReactNode } from "react";

import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { type Breakpoint, Stack, useTheme } from "@mui/material";

import type { RuleListUsage } from "./ruleListRows";

interface RuleExperiencesDialogProps {
  open: boolean;
  /** The headline; also the dialog's accessible name. */
  title: string;
  description: ReactNode;
  /** The experiences the decision touches. Empty renders no list panel. */
  experiences: readonly RuleListUsage[];
  /** The footer's buttons, in reading order. */
  actions: ReactNode;
  onClose: () => void;
  /** Widen past the default 460px when the footer holds three verbs. */
  maxWidth?: Breakpoint;
}

/**
 * The shared "this rule reaches these experiences, decide with that in front of
 * you" dialog: headline, one sentence of consequence, the experiences named in
 * a scrollable panel, then the verbs.
 *
 * It is deliberately presentational. Every caller already knows which
 * experiences a rule serves — the list rows carry `usedIn`, the workspace
 * carries the shared-rule experiences — so this owns the layout and nothing
 * else, and the same surface can say "saving changes these" or "deleting is
 * blocked by these" without either caller re-describing the panel.
 */
export const RuleExperiencesDialog = ({
  open,
  title,
  description,
  experiences,
  actions,
  onClose,
  maxWidth,
}: RuleExperiencesDialogProps) => {
  const theme = useTheme();

  return (
    <IgDialog
      showDialog={open}
      maxWidth={maxWidth}
      title={null}
      aria-label={title}
      showCloseIcon
      hideActions
      dontUseDialogContent
      rawContent={
        <Stack spacing={6} sx={{ minWidth: 0 }}>
          <Stack spacing={1} sx={{ pr: 8 }}>
            <IgTypography component="h2" variant="h3" fontWeight={600}>
              {title}
            </IgTypography>
            <IgTypography color="text.secondary" variant="body1">
              {description}
            </IgTypography>
          </Stack>

          {experiences.length > 0 ? (
            <Stack
              component="ul"
              spacing={1}
              sx={{
                m: 0,
                p: 4,
                maxHeight: 240,
                overflowY: "auto",
                overflowWrap: "anywhere",
                listStyle: "none",
                bgcolor: theme.igBackgroundColors.interactive.neutral.default,
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: 1,
              }}
            >
              {experiences.map((experience) => (
                <Stack component="li" key={experience.assignmentId}>
                  <IgTypography variant="body1">{experience.name}</IgTypography>
                  {/* Qualifies a usage that is not one experience — the
                      variation, or the placement it backstops store-wide — so
                      two rows that share a name stay distinguishable. */}
                  {experience.detail ? (
                    <IgTypography color="text.tertiary" variant="body2">
                      {experience.detail}
                    </IgTypography>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" justifyContent="flex-end" spacing={3}>
            {actions}
          </Stack>
        </Stack>
      }
      handleClose={() => onClose()}
    />
  );
};
